import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route,Switch } from 'react-router-dom';
import { useState, useEffect} from 'react';
import { configData } from "./config";
import { AuthProvider } from './components/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Header from './components/Header';
import Footer from './components/Footer';
import "./components/classes/tokens.css";
import Page from './components/Page';
import Loading from './components/Loading';
import { captureAttribution } from "./components/services/AttributionService";
import OldBrowserAlert from './components/OldBrowserAlert';
import PixelTracker from './components/PixelTracker';
import DefaultOgImage from './components/DefaultOgImage';
import ReactPixel from 'react-facebook-pixel';
import { hydrateGlobalContent, subscribeGlobalContent } from './components/PageEditor/globalContentStore';

// The other screens are loaded the first time they are shown, so that opening a page downloads
// and runs only what the pages need. Their styles all stay in the main stylesheet, in the
// order they had there (screenStyles.js).
const Login = lazy(() => import('./components/Login'));
const AgencyLogin = lazy(() => import('./components/AgencyLogin'));
const CartApp = lazy(() => import('./components/CartPage'));
const Post = lazy(() => import('./components/Post'));
const ApplicationForm = lazy(() => import('./components/Application'));
const Form = lazy(() => import('./components/Form'));
const RequestForm = lazy(() => import('./components/RequestForm'));
const TalkwithLynn = lazy(() => import('./components/TalkwithLynn'));
const CoursePay = lazy(() => import('./components/CoursePay'));
const CheckoutPage = lazy(() => import('./components/CheckoutPage'));
const PaymentPage = lazy(() => import('./components/PaymentPage'));
const PaypalPayment = lazy(() => import('./components/PaypalPayment'));
const PaypalPaymentNotify = lazy(() => import('./components/PaypalPaymentNotify'));
const SezzlePaymentNotify = lazy(() => import('./components/SezzlePaymentNotify'));
const ThankyouPage = lazy(() => import('./components/ThankyouPage'));
const LocalStorage = lazy(() => import('./components/LocalStorage'));
const ConferencePay = lazy(() => import('./components/ConferencePay'));
const CourseQuickPay = lazy(() => import('./components/CourseQuickPay'));
const AbandonedPay = lazy(() => import('./components/AbandonedPay'));
const MiscPay = lazy(() => import('./components/MiscPay'));
const ReenrollPay = lazy(() => import('./components/ReenrollPay'));
const MiscellaneousCharge = lazy(() => import('./components/MiscellaneousCharge'));
const AdvancedCertificate = lazy(() => import('./components/AdvancedCertificate'));
const SoccerAdvancedCertificate = lazy(() => import('./components/SoccerAdvancedCertificate'));
const BaseballAdvancedCertificate = lazy(() => import('./components/BaseballAdvancedCertificate'));
const BasketballAdvancedCertificate = lazy(() => import('./components/BasketballAdvancedCertificate'));
const FootballAdvancedCertificate = lazy(() => import('./components/FootballAdvancedCertificate'));
const HockeyAdvancedCertificate = lazy(() => import('./components/HockeyAdvancedCertificate'));
const SportsAgentAdvancedCertificate = lazy(() => import('./components/SportsAgentAdvancedCertificate'));
const Store = lazy(() => import('./components/Store'));
const ApparelProductDetail = lazy(() => import('./components/ApparelProductDetail'));
const ViewTab = lazy(() => import('./components/ViewTab'));
const UserProfile = lazy(() => import('./components/UserProfile'));
// A profile address (/users/<name>, /user/<uid>): the profile tabs for a signed-in visitor, else
// the public profile page, as the live site shows anyone (never the login page)
const ProfileRoute = () => (localStorage.getItem('uid') ? <ViewTab /> : <UserProfile />);
const EditProfile = lazy(() => import('./components/EditProfile'));
const CreditCardChange = lazy(() => import('./components/CreditCardChange'));
const CancelledSubscriptionCreditCardChange = lazy(() => import('./components/CancelledSubscriptionCreditCardChange'));
const SubscriptionUpdate = lazy(() => import('./components/SubscriptionUpdate'));
const CancelledSubscriptionUpdate = lazy(() => import('./components/CancelledSubscriptionUpdate'));
const OrderDetailsPage = lazy(() => import('./components/OrderDetailsPage'));
const ForgetPassword = lazy(() => import('./components/ForgetPassword'));
const VerifyPassword = lazy(() => import('./components/VerifyPassword'));
const Logout = lazy(() => import('./components/Logout'));
const SubscriptionEdit = lazy(() => import('./components/SubscriptionEdit'));
const AddToEarena = lazy(() => import('./components/AddToEarena'));
const AddAthlete = lazy(() => import('./components/AddAthlete'));
const AgentDirectory = lazy(() => import('./components/AgentDirectory'));
const ScoutDirectory = lazy(() => import('./components/ScoutDirectory'));
const ManageEmails = lazy(() => import('./components/ManageEmails'));
const ManagePeople = lazy(() => import('./components/ManagePeople'));
const ManageAdminPeople = lazy(() => import('./components/ManageAdminPeople'));
const AppList = lazy(() => import('./components/AppList'));
const AddUser = lazy(() => import('./components/AddUser'));
const EventlogMore = lazy(() => import('./components/EventlogMore'));
const ScheduleTaskMore = lazy(() => import('./components/ScheduleTaskMore'));
const MySalesTasks = lazy(() => import('./components/MySalesTasks'));
const ManageEmailsEdit = lazy(() => import('./components/ManageEmailsEdit'));
const ViewEmail = lazy(() => import('./components/ViewEmail'));
const ViewAthlete = lazy(() => import('./components/ViewAthlete'));
const SalespersonCreateApplication = lazy(() => import('./components/SalespersonCreateApplication'));
const ApplicationImporter = lazy(() => import('./components/ApplicationImporter'));
const RolodexImporter = lazy(() => import('./components/RolodexImporter'));
const ManageSortflag = lazy(() => import('./components/ManageSortflag'));
const SortflagUsers = lazy(() => import('./components/SortflagUsers'));
const PendingAthletes = lazy(() => import('./components/PendingAthletes'));
const CourseCodeKey = lazy(() => import('./components/CourseCodeKey'));
const DraftDivvy = lazy(() => import('./components/DraftDivvy'));
const ViewScoutingRequests = lazy(() => import('./components/ViewScoutingRequests'));
const AddSortflag = lazy(() => import('./components/AddSortflag'));
const EditSortflag = lazy(() => import('./components/EditSortflag'));
const ViewSortflag = lazy(() => import('./components/ViewSortflag'));
const EditAgentMessage = lazy(() => import('./components/EditAgentMessage'));
const EditScoutMessage = lazy(() => import('./components/EditScoutMessage'));
const ManageScoutingReportsEdit = lazy(() => import('./components/ManageScoutingReportsEdit'));
const ManageAgents = lazy(() => import('./components/ManageAgents'));
const ManageAthletes = lazy(() => import('./components/ManageAthletes'));
const ManageAgentMessage = lazy(() => import('./components/ManageAgentMessage'));
const CreateAgentMessage = lazy(() => import('./components/CreateAgentMessage'));
const ManageScoutMessage = lazy(() => import('./components/ManageScoutMessage'));
const CreateScoutMessage = lazy(() => import('./components/CreateScoutMessage'));
const ManageScoutingReports = lazy(() => import('./components/ManageScoutingReports'));
const AgentBio = lazy(() => import('./components/AgentBio'));
const ScoutBio = lazy(() => import('./components/ScoutBio'));
const AthleteBio = lazy(() => import('./components/AthleteBio'));
const AppsByCountry = lazy(() => import('./components/AppsByCountry'));
const AttributionReport = lazy(() => import('./components/AttributionReport'));
const PaymentsReport = lazy(() => import('./components/PaymentsReport'));
const CancelledPaymentsReport = lazy(() => import('./components/CancelledPaymentsReport'));
const SortFlagReport = lazy(() => import('./components/SortFlagReport'));
const UserAdvanceSearch = lazy(() => import('./components/UserAdvanceSearch'));
const ReferralWalletReport = lazy(() => import('./components/ReferralWalletReport'));
const PaymentsComparisonReport = lazy(() => import('./components/PaymentsComparisonReport'));
const SalesReportBySortflag = lazy(() => import('./components/SalesReportBySortflag'));
const SuspendedPaymentReport = lazy(() => import('./components/SuspendedPaymentReport'));
const SalespersonActivityReport = lazy(() => import('./components/SalespersonActivityReport'));
const SalesReportByProduct = lazy(() => import('./components/SalesReportByProduct'));
const SalesandAppsReport = lazy(() => import('./components/SalesandAppsReport'));
const ReferrerLog = lazy(() => import('./components/ReferrerLog'));
const ResourceLibrary = lazy(() => import('./components/ResourceLibrary'));
const ResourceLibraryEdit = lazy(() => import('./components/ResourceLibraryEdit'));
const ResourceLibraryCreate = lazy(() => import('./components/ResourceLibraryCreate'));
const ManageGlobalAmbassadors = lazy(() => import('./components/ManageGlobalAmbassadors'));
const AddContent = lazy(() => import('./components/AddContent'));
const AddNews = lazy(() => import('./components/AddNews'));
const ManageNews = lazy(() => import('./components/ManageNews'));
const BlogHome = lazy(() => import('./components/BlogHome'));
const BlogFeed = lazy(() => import('./components/BlogHome').then((module) => ({ default: module.BlogFeed })));
const SmwwBlogList = lazy(() => import('./components/SmwwBlogList'));
const SmwwNewsList = lazy(() => import('./components/SmwwNewsList'));
const ManageConferenceSpeakers = lazy(() => import('./components/ManageConferenceSpeakers'));
const ConferenceSpeakerForm = lazy(() => import('./components/ConferenceSpeakerForm'));
const AlumniWebformResults = lazy(() => import('./components/AlumniWebformResults'));
const AlumniSubmission = lazy(() => import('./components/AlumniSubmission'));
const ManageReferrals = lazy(() => import('./components/ManageReferrals'));
const ManageOrders = lazy(() => import('./components/ManageOrders'));
const ManageProducts = lazy(() => import('./components/ManageProducts'));
const CourseMentors = lazy(() => import('./components/CourseMentors'));
const CourseMentorEdit = lazy(() => import('./components/CourseMentorEdit'));
const AddNewProduct = lazy(() => import('./components/AddNewProduct'));
const EditProduct = lazy(() => import('./components/EditProduct'));
const CashOut = lazy(() => import('./components/CashOut'));
const ApparelOrderItemList = lazy(() => import('./components/ApparelOrderItemList'));
const ChangeApparelOrderStatus = lazy(() => import('./components/ChangeApparelOrderStatus'));
const ApparelOrderStatusLog = lazy(() => import('./components/ApparelOrderStatusLog'));
const ManageCategories = lazy(() => import('./components/ManageCategories'));
const CreateProductCategory = lazy(() => import('./components/CreateProductCategory'));
const EditProductCategory = lazy(() => import('./components/EditProductCategory'));
const FlatShippingRates = lazy(() => import('./components/FlatShippingRates'));
const CouponList = lazy(() => import('./components/CouponList'));
const CreateCoupon = lazy(() => import('./components/CreateCoupon'));
const EditCoupon = lazy(() => import('./components/EditCoupon'));
const DeleteCoupon = lazy(() => import('./components/DeleteCoupon'));
const DiscountList = lazy(() => import('./components/DiscountList'));
const CreateNewDiscount = lazy(() => import('./components/CreateNewDiscount'));
const EditDiscount = lazy(() => import('./components/EditDiscount'));
const DeleteDiscount = lazy(() => import('./components/DeleteDiscount'));
const ConferenceRegistrationReport = lazy(() => import('./components/ConferenceRegistrationReport'));
const ScoutAgentAthleteExport = lazy(() => import('./components/ScoutAgentAthleteExport'));
const ListAmbassador = lazy(() => import('./components/ListAmbassador'));
const ListContinentAmbassador = lazy(() => import('./components/ListContinentAmbassador'));
const AmbassadorBio = lazy(() => import('./components/AmbassadorBio'));
const SmwwTestimonialsList = lazy(() => import('./components/SmwwTestimonialsList'));
const DeleteProduct = lazy(() => import('./components/DeleteProduct'));
const PackageList = lazy(() => import('./components/PackageList'));
const EditProductPackage = lazy(() => import('./components/EditProductPackage'));
const DeleteProductPackage = lazy(() => import('./components/DeleteProductPackage'));
const CreateProductPackage = lazy(() => import('./components/CreateProductPackage'));
const AgentMyAccount = lazy(() => import('./components/AgentMyAccount'));
const ManageMyAthletes = lazy(() => import('./components/ManageMyAthletes'));
const AgentHome = lazy(() => import('./components/AgentHome'));
const CreateScoutReport = lazy(() => import('./components/CreateScoutReport'));
const ScoutMyReports = lazy(() => import('./components/ScoutMyReports'));
const ScoutMyAccount = lazy(() => import('./components/ScoutMyAccount'));
const MyScoutRequestReports = lazy(() => import('./components/MyScoutRequestReports'));
const ViewScoutingRequestsScout = lazy(() => import('./components/ViewScoutingRequestsScout'));
const ScoutRequestReportView = lazy(() => import('./components/ScoutRequestReportView'));
const ScoutingRequestsFrom = lazy(() => import('./components/ScoutingRequestsForm'));
const SportsEducationProgram = lazy(() => import('./components/SportsEducationProgram'));
const ScoutMainHome = lazy(() => import('./components/ScoutMainHome'));
const ContinentsScouts = lazy(() => import('./components/ContinentsScouts'));
const ApprovalProfileChanges = lazy(() => import('./components/ApprovalProfileChanges'));
const LookingForAgent = lazy(() => import('./components/LookingForAgent'));
const BasketballEducationProgram = lazy(() => import('./components/BasketballEducationProgram'));
const CricketEducationProgram = lazy(() => import('./components/CricketEducationProgram'));
const FootballEducationProgram = lazy(() => import('./components/FootballEducationProgram'));
const HockeyEducationProgram = lazy(() => import('./components/HockeyEducationProgram'));
const RugbyEducationProgram = lazy(() => import('./components/RugbyEducationProgram'));
const SoccerEducationProgram = lazy(() => import('./components/SoccerEducationProgram'));
const AgencyMainHome = lazy(() => import('./components/AgencyMainHome'));
const CanadaScoutList = lazy(() => import('./components/CanadaScoutList'));
const CanadaStateScoutList = lazy(() => import('./components/CanadaStateScoutList'));
const ScoutsMap = lazy(() => import('./components/ScoutsMap'));
const ListScoutSports = lazy(() => import('./components/ListScoutSports'));
const ListContinentScoutSports = lazy(() => import('./components/ListContinentScoutSports'));
const AllScouts = lazy(() => import('./components/AllScouts'));
const USScoutList = lazy(() => import('./components/USScoutList'));
const USStateScoutList = lazy(() => import('./components/USStateScoutList'));
const ContactUs = lazy(() => import('./components/ContactUs'));
const AgentsMap = lazy(() => import('./components/AgentsMap'));
const AgencyWebsiteandCommunications = lazy(() => import('./components/AgencyWebsiteandCommunications'));
const AgencyFinancialServices = lazy(() => import('./components/AgencyFinancialServices'));
const ContinentsAgents = lazy(() => import('./components/ContinentsAgents'));
const CanadaAgentList = lazy(() => import('./components/CanadaAgentList'));
const CanadaStateAgentList = lazy(() => import('./components/CanadaStateAgentList'));
const USAgentList = lazy(() => import('./components/USAgentList'));
const USStateAgentList = lazy(() => import('./components/USStateAgentList'));
const AllAgents = lazy(() => import('./components/AllAgents'));
const SportSpecificAgent = lazy(() => import('./components/SportSpecificAgent'));
const BaseballAgency = lazy(() => import('./components/BaseballAgency'));
const BasketballAgency = lazy(() => import('./components/BasketballAgency'));
const CricketAgency = lazy(() => import('./components/CricketAgency'));
const FootballAgency = lazy(() => import('./components/FootballAgency'));
const HockeyAgency = lazy(() => import('./components/HockeyAgency'));
const RacingAgency = lazy(() => import('./components/RacingAgency'));
const RugbyAgency = lazy(() => import('./components/RugbyAgency'));
const SoccerAgency = lazy(() => import('./components/SoccerAgency'));
const AgencyEndorsements = lazy(() => import('./components/AgencyEndorsements'));
const AgencyMissionandVision = lazy(() => import('./components/AgencyMissionandVision'));
const AgencySMWWDifference = lazy(() => import('./components/AgencySMWWDifference'));
const AgencyPostCareerPlanning = lazy(() => import('./components/AgencyPostCareerPlanning'));
const AgencyMarketing = lazy(() => import('./components/AgencyMarketing'));
const LookingForAgentView = lazy(() => import('./components/LookingForAgentView'));
const ManageLookingforAthleteSubmissions = lazy(() => import('./components/ManageLookingforAthleteSubmissions'));
const AthleteRequestApproval = lazy(() => import('./components/AthleteRequestApproval'));
const AgencyNewsList = lazy(() => import('./components/AgencyNewsList'));
const AddBlogPost = lazy(() => import('./components/AddBlogPost'));
const AgencyNewsContent = lazy(() => import('./components/AgencyNewsContent'));
const ManageBlogPost = lazy(() => import('./components/ManageBlogPost'));
const EditBlog = lazy(() => import('./components/EditBlog'));
const MyCredentialsDetailView = lazy(() => import('./components/MyCredentialsDetailView'));
const EditOrderInfoPage = lazy(() => import('./components/EditOrderInfoPage'));
const ManageRequestScoutReportSubmissions = lazy(() => import('./components/ManageRequestScoutReportSubmissions'));
const ScoutRequestReportEdit = lazy(() => import('./components/ScoutRequestReportEdit'));
const LookingForAgentEdit = lazy(() => import('./components/LookingForAgentEdit'));
const ScoutRequestWebformResult = lazy(() => import('./components/ScoutRequestWebformResult'));
const LookingForAgentWebformResults = lazy(() => import('./components/LookingForAgentWebformResults'));
const CheckoutOrderLog = lazy(() => import('./components/CheckoutOrderLog'));
const ManageDocTalks = lazy(() => import('./components/ManageDocTalks'));
const AddDocTalk = lazy(() => import('./components/AddDocTalk'));
const EditDocTalk = lazy(() => import('./components/EditDocTalk'));
const ViewDocTalk = lazy(() => import('./components/ViewDocTalk'));
const ManageCoursePages = lazy(() => import('./components/ManageCoursePages'));
const AddNewCourse = lazy(() => import('./components/AddNewCourse'));
const AddANewConferencePage = lazy(() => import('./components/AddANewConferencePage'));
const ManageTestimonials = lazy(() => import('./components/ManageTestimonials'));
const AddTestimonialPage = lazy(() => import('./components/AddTestimonialPage'));
const ManageSuccessStories = lazy(() => import('./components/ManageSuccessStories'));
const AddSuccessStoryPage = lazy(() => import('./components/AddSuccessStoryPage'));
const PageEditor = lazy(() => import('./components/PageEditor/PageEditor'));
const ManageVisualPages = lazy(() => import('./components/ManageVisualPages'));
const AddVisualPage = lazy(() => import('./components/AddVisualPage'));
const GlobalCourseContent = lazy(() => import('./components/GlobalCourseContent'));

const currentDomain = window.location.host.replace(/^https?:\/\//, '');
const agencydomains = [
  configData.RSMWW_AGENCY_DOMAIN,
  configData.RSMWW_AGENCY_DOMAIN1
];
// The SMWW Agency and SMWW Scout sites (this app on their own hosts)
const isAgencyOrScoutSite = [
  ...agencydomains,
  configData.RSMWW_SCOUT_DOMAIN,
  configData.RSMWW_SCOUT_DOMAIN1,
  configData.RSMWW_SCOUT_DOMAIN2
].some(domain => currentDomain.includes(domain));
const App = () => {
   const [isLoggedIn, setIsLoggedIn] = useState(
    !!localStorage.getItem("uid")
  );
   // Hydrate the shared Global Course Content values once at app boot, and
   // re-render when they land (or when an admin save updates them) so course
   // pages pick up the live values. Failure is silent — the store keeps its
   // compile-time fallbacks, so pages always render.
   const [, setGlobalContentTick] = useState(0);
   useEffect(() => {
     const unsub = subscribeGlobalContent(() => setGlobalContentTick((t) => t + 1));
     hydrateGlobalContent();
     return unsub;
   }, []);
   useEffect(() => {
  const host = window.location.hostname;

  let favicon = "/favicon.ico";
  let appleIcon = "/favicon.png";

  if (agencydomains.some(domain => host.includes(domain))) {
    favicon = "/agency_favicon.ico";
    appleIcon = "/agency_favicon.png";
  }

  // Update favicon
  const faviconLink = document.querySelector("link[rel='icon']");
  if (faviconLink) {
    faviconLink.href = favicon + "?v=" + Date.now();
  }

  // Update apple touch icon
  const appleLink = document.querySelector("link[rel='apple-touch-icon']");
  if (appleLink) {
    appleLink.href = appleIcon + "?v=" + Date.now();
  }

   // Logout sync across tabs
  const syncLogout = (event) => {
    if (event.key === "logout" || event.key === "uid") {

      const uid = localStorage.getItem("uid");

     if (!uid) {
                // update auth state only
                setIsLoggedIn(false);
            }
    }
  };

  window.addEventListener("storage", syncLogout);

  return () => {
    window.removeEventListener("storage", syncLogout);
  };
  
}, []);
 useEffect(() => {
    if (process.env.NODE_ENV !== 'development') {
      ReactPixel.init(configData.FB_PIXEDL_ID);
    }
   captureAttribution();

  }, []);
  return (
    <AuthProvider>

    <Router>
      <div>
        <OldBrowserAlert />
         <PixelTracker />
        <DefaultOgImage />
        <Header />
        {/* The loader shows while a screen's code is fetched, the first time it is opened */}
        <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={
    currentDomain.includes(configData.RSMWW_SCOUT_DOMAIN) || currentDomain.includes(configData.RSMWW_SCOUT_DOMAIN1) || currentDomain.includes(configData.RSMWW_SCOUT_DOMAIN2)  ? (
      <ScoutMainHome />
    ) : currentDomain.includes(configData.RSMWW_AGENCY_DOMAIN) || currentDomain.includes(configData.RSMWW_AGENCY_DOMAIN1) ? (
            <AgencyMainHome />
          ) : (
      <Page /> // Main site: home page (configData.HOME_PAGE_PATH)
    )
  } 
/>
           <Route path="/agencyhome" element={<AgencyMainHome />} />
           <Route path="/scouthome" element={<ScoutMainHome />} />
          <Route path="/user" element={<Login />} />
          <Route path="/agency/login" element={<AgencyLogin />} />
          <Route path="/smww/cart" element={<CartApp />} />
          <Route path="/post/:id" element={<Post />} />
          <Route path="/application" element={<ApplicationForm />} /> 
          <Route path="/form" element={<Form />} />
          <Route path="/request-more-info" element={<RequestForm />} />
          {/* On the main site this and /contact are the CMS's basic pages (nodes 606 and 82), with View
              and Edit tabs for admins as on the live site; the Agency and Scout sites keep their own */}
          <Route path="/talk-with-dr-lynn-lashbrook" element={isAgencyOrScoutSite ? <TalkwithLynn /> : <Page />} />
          <Route path='/smww_pay' element={<CoursePay />} />
          <Route path='/smww/checkout' element={<CheckoutPage />} />
		      <Route path='/smww/checkout/cart' element={<PaymentPage />} />
          <Route path='/smww/checkout/paypal' element={<PaypalPayment />} />
          <Route path='/smww/checkout/paypal/notify' element={<PaypalPaymentNotify />} />
          <Route path='/smww/checkout/sezzle/confirm' element={<SezzlePaymentNotify />} />
		      <Route path='/smww/checkout/thankyou/:order_id' element={<ThankyouPage />} />
          <Route path='/smww/get/userorder' element={<LocalStorage />} />
          <Route path='/smww_conference_pay' element={<ConferencePay />} />
          <Route path='/smww_course_pay' element={<CourseQuickPay />} />
          <Route path='/smww_abandoned_pay' element={<AbandonedPay />} />
          <Route path='/smww_misc_pay' element={<MiscPay />} />
          <Route path='/smww_reenroll_pay' element={<ReenrollPay />} />
          <Route path='/miscellaneous/charge/:uid' element={<MiscellaneousCharge />} />
          <Route path='/advanced_certificates' element={<AdvancedCertificate />} />
          <Route path='/soccer-advanced-certificate' element={<SoccerAdvancedCertificate />} />
          <Route path='/baseball-advanced-certificate' element={<BaseballAdvancedCertificate />} />
          <Route path='/basketball-advanced-certificate' element={<BasketballAdvancedCertificate />} />
          <Route path='/football-advanced-certificate' element={<FootballAdvancedCertificate />} />
          <Route path='/hockey-advanced-certificate' element={<HockeyAdvancedCertificate />} />
          <Route path='/sports-agent-advanced-certificate' element={<SportsAgentAdvancedCertificate />} />
          <Route path='/store' element={<Store />} />
          <Route path='/store/:slug' element={<ApparelProductDetail />} />
          <Route path='/user/:uid' element={<ProfileRoute />} />
          <Route path='/users/:username' element={<ProfileRoute />} />
          <Route path='/user/:uid/:tab' element={<ViewTab />} />
          <Route path='/subscription/card/change/:subscription_id' element={<CreditCardChange />} />
          <Route path='/cancelled_subscription/card/change/:subscription_id' element={<CancelledSubscriptionCreditCardChange />} />
          <Route path='/subscription/update/:subscription_id' element={<SubscriptionUpdate /> } />
          <Route path='/cancelled_subscription/update/:subscription_id' element={<CancelledSubscriptionUpdate /> } />
          <Route path='/subscription/edit-status/:subscription_id/:id' element={<SubscriptionEdit /> } />
          <Route path='/user/:uid/smww_orders/:order_id' element={<OrderDetailsPage /> } />
          <Route path='/user/password' element={<ForgetPassword /> } />
          <Route path='/user/verify_password' element={<VerifyPassword /> } />
          <Route path='/user/logout' element={<Logout /> } />
          <Route path='/add_to_earena/:uid' element={<AddToEarena /> } />
          <Route path='/agency_home' element={<AgentHome /> } />
          <Route path='/scouting-requests' element={<ScoutingRequestsFrom /> } />
          <Route path='/lookingforagent.php' element={<LookingForAgent /> } />
          
          <Route path='/sports/education/baseball' element={<SportsEducationProgram /> } />
		      <Route path='/sports/education/basketball' element={<BasketballEducationProgram /> } />
          <Route path='/sports/education/cricket' element={<CricketEducationProgram/> } />
          <Route path='/sports/education/football' element={<FootballEducationProgram/> } />
          <Route path='/sports/education/rugby' element={<RugbyEducationProgram/> } />
          <Route path='/sports/education/soccer' element={<SoccerEducationProgram/> } />
          <Route path='/sports/education/hockey' element={<HockeyEducationProgram /> } />

          <Route path='/agent/:slug' element={<AgentBio /> } />
          <Route path='/scout/:slug' element={<ScoutBio /> } />
          <Route path='/athlete/:slug' element={<AthleteBio /> } />
          
          <Route path='/program/global-ambassadors' element={<ListAmbassador /> } />
          <Route path='/continents/:continentid/ambassadors' element={<ListContinentAmbassador /> } />
          <Route path='/ambassador/:slug' element={<AmbassadorBio /> } />
          <Route path='/smww-testimonials' element={<SmwwTestimonialsList /> } />
          <Route path='/continents/:continentid/scouts' element={<ContinentsScouts /> } />
          <Route path='/sports-scout-canada-map' element={<CanadaScoutList /> } />
          <Route path='/sports-scout-canada-map/:state' element={<CanadaStateScoutList /> } />
          <Route path='/sports_scouts_map' element={<ScoutsMap /> } />
           <Route path='/scout-sports/:sports' element={<ListScoutSports /> } />
          <Route path='/scout-sports/:sports/:continentid' element={<ListContinentScoutSports /> } />
          <Route path='/scouts' element={<AllScouts /> } />
           <Route path='/sports_scouts_usmap' element={<USScoutList /> } />
          <Route path='/sports_scouts_usmap/:sports' element={<USScoutList /> } />
          <Route path='/sports_scouts_usmap/:sports/:state' element={<USStateScoutList /> } />
          <Route path='/contact' element={isAgencyOrScoutSite ? <ContactUs /> : <Page /> } />
          <Route path='/sports_agents_map' element={<AgentsMap /> } />
           <Route path='/website-and-new-media-communications' element={<AgencyWebsiteandCommunications /> } />
           <Route path='/financial-services' element={<AgencyFinancialServices /> } />
          <Route path='/continents/:continentid/agents' element={<ContinentsAgents /> } />
          <Route path='/sports_agent_canada_map' element={<CanadaAgentList /> } />
          <Route path='/sports_agent_canada_map/:state' element={<CanadaStateAgentList /> } />
          <Route path='/sports_agents_usmap' element={<USAgentList /> } />
          <Route path='/sports_agents_usmap/:sports' element={<USAgentList /> } />
          <Route path='/state/:state/agents' element={<USStateAgentList /> } />
          <Route path='/agents' element={<AllAgents /> } />
           <Route path='/agents/:sport' element={<SportSpecificAgent /> } />
          <Route path='/sports/baseball' element={<BaseballAgency/> } />
          <Route path='/sports/basketball' element={<BasketballAgency/> } />
          <Route path='/sports/cricket' element={<CricketAgency/> } />
          <Route path='/sports/football' element={<FootballAgency/> } />
          <Route path='/sports/hockey' element={<HockeyAgency/> } />
          <Route path='/sports/racing' element={<RacingAgency/> } />
          <Route path='/sports/rugby' element={<RugbyAgency/> } />
          <Route path='/sports/soccer' element={<SoccerAgency/> } />
          <Route path='/content/marketing' element={<AgencyMarketing /> } />
          <Route path='/sports/basketball/endorsements' element={<AgencyEndorsements /> } />
          <Route path='/content/smww-difference' element={<AgencySMWWDifference /> } />
          <Route path='/our-mission-and-values' element={<AgencyMissionandVision /> } />
          <Route path='/sports/basketball/post_career_planning' element={<AgencyPostCareerPlanning /> } />
          <Route path='/smww-agency-news' element={< AgencyNewsList /> } />
          <Route path='/blog-content/:slug' element={< AgencyNewsContent /> } />
           <Route path='/certificate/:slug/:drupal_uid/course/:sku/:classid' element={<MyCredentialsDetailView />} />
          
         <Route path='/node/add/blog'  element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
           <AddBlogPost />
          </ProtectedRoute>
          }/>
           <Route path='/blog-content/:id/edit'  element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
           <EditBlog />
          </ProtectedRoute>
          }/>
           <Route path='/user/:uid/edit_order/:order_id'  element={
          <ProtectedRoute roles={['administrator','sales people','SAAdmin']}>
           <EditOrderInfoPage />
          </ProtectedRoute>
          }/>
          <Route path='manage-blog'  element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
           <ManageBlogPost />
          </ProtectedRoute>
          }/>
          
           <Route path='/node/1080/submission/:id'  element={
          <ProtectedRoute roles={['administrator','SAAdmin','agent']}>
           <LookingForAgentView />
          </ProtectedRoute>
          }/>
           <Route path='/node/1080/submissions'  element={
          <ProtectedRoute roles={['administrator','SAAdmin','agent']}>
           <ManageLookingforAthleteSubmissions />
          </ProtectedRoute>
          }/>
          <Route path='/node/841/submissions'  element={
          <ProtectedRoute roles={['administrator','SAAdmin','scout']}>
           <ManageRequestScoutReportSubmissions />
          </ProtectedRoute>
          }/>
          <Route path='/athlete/requestapproval/:drupal_uid'  element={
          <ProtectedRoute roles={['administrator','SAAdmin','agent']}>
           <AthleteRequestApproval />
          </ProtectedRoute>
          }/>
           <Route path='/add_athlete'  element={
          <ProtectedRoute roles={['administrator','SAAdmin','sales people','agent']}>
           <AddAthlete />
          </ProtectedRoute>
          }/>
           <Route path='/agents-directory'  element={
          <ProtectedRoute roles={['administrator','SAAdmin','sales people','agent']}>
           <AgentDirectory />
          </ProtectedRoute>
          }/>
          <Route path='/scouts-directory'  element={
          <ProtectedRoute roles={['administrator','SAAdmin','sales people','scout']}>
           <ScoutDirectory />
          </ProtectedRoute>
          }/>
           <Route path='/manage-emails'  element={
          <ProtectedRoute roles={['administrator','SAAdmin','sales people']}>
            <ManageEmails />
          </ProtectedRoute>
          }/>
          <Route path='/saadmin-people'  element={
          <ProtectedRoute roles={['administrator','SAAdmin','sales people']}>
            <ManagePeople />
          </ProtectedRoute>
          }/>
           <Route path='/manage/people'  element={
          <ProtectedRoute roles={['administrator']}>
            <ManageAdminPeople />
          </ProtectedRoute>
          }/>
          <Route path='/app-list'  element={
          <ProtectedRoute roles={['administrator','SAAdmin','sales people']}>
            <AppList />
          </ProtectedRoute>
          }/>
          <Route path='/admin/people/create'  element={
          <ProtectedRoute roles={['administrator','SAAdmin','sales people']}>
            <AddUser />
          </ProtectedRoute>
          }/>
          <Route path='/salesperson/create/application'  element={
          <ProtectedRoute roles={['administrator','SAAdmin','sales people']}>
            <SalespersonCreateApplication />
          </ProtectedRoute>
          }/>
          <Route path='/eventlog/more/:uid' element={<EventlogMore /> } />
          <Route path='/moretasks/:uid' element={<ScheduleTaskMore /> } />
         
         <Route path='/my_sales_tasks'  element={
          <ProtectedRoute roles={['administrator','SAAdmin','sales people']}>
            <MySalesTasks />
          </ProtectedRoute>
          }/>
          <Route path='/emails/:id/:tab'  element={
          <ProtectedRoute roles={['administrator','SAAdmin','sales people']}>
            <ViewEmail />
          </ProtectedRoute>
          }/>
         <Route path='/view_athlete/:drupal_uid' element={
          <ProtectedRoute roles={['administrator','SAAdmin','sales people', 'agent']}>
            <ViewAthlete /></ProtectedRoute>
          } />
          <Route path='/importer/application'  element={
          <ProtectedRoute roles={['administrator','SAAdmin','sales people']}>
            <ApplicationImporter />
          </ProtectedRoute>
          }/>
           <Route path='/importer/rolodex'  element={
           <ProtectedRoute roles={['administrator','SAAdmin','sales people']}>
            <RolodexImporter />
          </ProtectedRoute>
          }/>
          <Route path='/draft-divvy'  element={
           <ProtectedRoute roles={['administrator','SAAdmin','sales people']}>
            <DraftDivvy />
          </ProtectedRoute>
          }/>
          <Route path='/sortflags'  element={
           <ProtectedRoute roles={['administrator','SAAdmin','sales people']}>
            <ManageSortflag />
          </ProtectedRoute>
          }/>
          <Route path='/sort-flag-users/:tid'  element={
           <ProtectedRoute roles={['administrator','SAAdmin','sales people']}>
            <SortflagUsers />
          </ProtectedRoute>
          }/>
        
          
          <Route path='/course_code_key'  element={
           <ProtectedRoute roles={['administrator','SAAdmin','sales people']}>
            <CourseCodeKey />
          </ProtectedRoute>
          }/>
           <Route path='/pending_athletes'  element={
           <ProtectedRoute roles={['administrator','SAAdmin','sales people','agent']}>
            <PendingAthletes />
          </ProtectedRoute>
          }/>
          <Route path='/pending_athletes/:id'  element={
           <ProtectedRoute roles={['administrator','SAAdmin','sales people','agent']}>
            <PendingAthletes />
          </ProtectedRoute>
          }/>
          
         

         <Route path='/view/scouting-requests'  element={
           <ProtectedRoute roles={['administrator','SAAdmin','sales people']}>
            <ViewScoutingRequests />
          </ProtectedRoute>
          }/>
         <Route path='/manage-doc-talk-mentors'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            <ManageDocTalks />
          </ProtectedRoute>
          }/>
           <Route path='/node/add/doc-talk-mentors'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            <AddDocTalk />
          </ProtectedRoute>
          }/>
          <Route path='/node/add/doc-talk-mentors'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            <AddDocTalk />
          </ProtectedRoute>
          }/>
          <Route path='/doc-talk-mentors/:nid/:tab'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            <EditDocTalk />
          </ProtectedRoute>
          }/>
          
          <Route path='/admin/smww-commerce/product-delete/:id'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            <DeleteProduct />
          </ProtectedRoute>
          }/>
          
           <Route path='/admin/structure/taxonomy/sort_flags/add'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            <AddSortflag />
          </ProtectedRoute>
          
          }/>
         
          <Route path='/taxonomy/term/:tid/:tab'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            <ViewSortflag />
          </ProtectedRoute>
          
          }/>
         <Route path='/manage-agents'  element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
            <ManageAgents />
          </ProtectedRoute>
          }/>
          <Route path='/manage-athletes'  element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
            <ManageAthletes />
          </ProtectedRoute>
          }/>
           <Route path='/saadmin/manage-agent-message'  element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
            <ManageAgentMessage />
          </ProtectedRoute>
          }/>
           <Route path='/node/add/agent-message'  element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
            <CreateAgentMessage />
          </ProtectedRoute>
          }/>
          
          <Route path='/agent-message/:aid/:tab'  element={
           <ProtectedRoute roles={['administrator','SAAdmin','agent']}>
            <EditAgentMessage />
          </ProtectedRoute>
          
          }/>
           <Route path='/saadmin/manage-scout-message'  element={
          <ProtectedRoute  roles={['administrator','SAAdmin']}>
            <ManageScoutMessage />
          </ProtectedRoute>
          }/>
           <Route path='/node/add/scout-message'  element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
            <CreateScoutMessage />
          </ProtectedRoute>
          }/>
          
          <Route path='/scout-message/:aid/:tab'  element={
           <ProtectedRoute roles={['administrator','SAAdmin','scout']}>
            <EditScoutMessage />
          </ProtectedRoute>
          
          }/>
           <Route path='/manage-scouting-reports'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            <ManageScoutingReports />
          </ProtectedRoute>
          
          }/>
          <Route path='/scout-report/:rid/:tab'  element={
           <ProtectedRoute roles={['administrator','SAAdmin','scout']}>
            <ManageScoutingReportsEdit />
          </ProtectedRoute>
          
          }/>
           <Route path='/admin/reports/apps_by_country'  element={
          <ProtectedRoute roles={['administrator','SAAdmin','sales people']}>
            <AppsByCountry />
          </ProtectedRoute>
          }/>
         <Route path='/admin/reports/courses-payments'  element={
          <ProtectedRoute roles={['administrator','SAAdmin','sales people']}>
            <PaymentsReport />
          </ProtectedRoute>
          }/>
          <Route path='/admin/reports/attribution'  element={
          <ProtectedRoute roles={['administrator','SAAdmin','sales people']}>
            <AttributionReport />
          </ProtectedRoute>
          }/>
           <Route path='/subscription/payment/cancelled'  element={
          <ProtectedRoute roles={['administrator','SAAdmin','sales people']}>
            <CancelledPaymentsReport />
          </ProtectedRoute>
          }/>
           <Route path='/admin/reports/sort_flag'  element={
          <ProtectedRoute roles={['administrator','SAAdmin','sales people']}>
            <SortFlagReport />
          </ProtectedRoute>
          }/>
           <Route path='/user-advance-search'  element={
          <ProtectedRoute roles={['administrator','SAAdmin','sales people']}>
            <UserAdvanceSearch />
          </ProtectedRoute>
          }/>
        
        <Route path='/admin/reports/refererrer_bonus'  element={
          <ProtectedRoute roles={['administrator','SAAdmin','sales people']}>
            <ReferralWalletReport />
          </ProtectedRoute>
          }/>
         <Route path='/admin/reports/sales_details_new'  element={
          <ProtectedRoute roles={['administrator','SAAdmin','sales people']}>
            <PaymentsComparisonReport />
          </ProtectedRoute>
          }/>
           <Route path='/admin/reports/sales_report_by_sortflag'  element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
            <SalesReportBySortflag />
          </ProtectedRoute>
          }/>
           <Route path='/admin/reports/suspended_payment'  element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
            <SuspendedPaymentReport />
          </ProtectedRoute>
          }/>
          <Route path='/admin/reports/event_log'  element={
          <ProtectedRoute roles={['administrator','SAAdmin','sales people']}>
            <SalespersonActivityReport />
          </ProtectedRoute>
          }/>
         <Route path='/admin/reports/sales_report_by_product'  element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
            <SalesReportByProduct />
          </ProtectedRoute>
          }/>
           <Route path='/admin/reports/weekly_sales_report'  element={
          <ProtectedRoute roles={['administrator']}>
            <SalesandAppsReport />
          </ProtectedRoute>
          }/>
          <Route path='/smww/referral/log/:drupaluid'  element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
            <ReferrerLog />
          </ProtectedRoute>
          }/>
          <Route path='/resource_library'  element={
          <ProtectedRoute roles={['administrator','SAAdmin','agent','scout']}>
            <ResourceLibrary />
          </ProtectedRoute>
          }/>
          <Route path='/resource_library/:rid/:tab'  element={
           <ProtectedRoute roles={['administrator','SAAdmin','agent','scout']}>
            <ResourceLibraryEdit />
          </ProtectedRoute>
          
          }/>
           <Route path='/node/add/resource-library'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < ResourceLibraryCreate />
          </ProtectedRoute>
          
          }/>
           <Route path='/manage-global-ambassadors'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < ManageGlobalAmbassadors />
          </ProtectedRoute>

          }/>
          {/* Admin dashboard screens that were Drupal pages */}
          <Route path='/node/add' element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
           <AddContent />
          </ProtectedRoute>
          }/>
          <Route path='/node/add/smww-news' element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
           <AddNews />
          </ProtectedRoute>
          }/>
          <Route path='/smww-news/:id/edit' element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
           <AddNews />
          </ProtectedRoute>
          }/>
          <Route path='/manage-news' element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
           <ManageNews />
          </ProtectedRoute>
          }/>
          <Route path='/blog' element={<BlogHome />} />
          <Route path='/blog/feed' element={<BlogFeed />} />
          <Route path='/smww-blog' element={<SmwwBlogList />} />
          <Route path='/smww-news' element={<SmwwNewsList />} />
          <Route path='/manage-conference-speakers' element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
           <ManageConferenceSpeakers />
          </ProtectedRoute>
          }/>
          <Route path='/node/add/conference-speakers' element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
           <ConferenceSpeakerForm />
          </ProtectedRoute>
          }/>
          <Route path='/conference-speakers/:id/edit' element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
           <ConferenceSpeakerForm />
          </ProtectedRoute>
          }/>
          <Route path='/node/3455/webform-results' element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
           <AlumniWebformResults />
          </ProtectedRoute>
          }/>
          <Route path='/node/3455/submission/:sid/:tab?' element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
           <AlumniSubmission />
          </ProtectedRoute>
          }/>
       <Route path='/manage-referrals'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < ManageReferrals />
          </ProtectedRoute>
          
          }/>
           <Route path='/admin/smww-commerce/order-list'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < ManageOrders />
          </ProtectedRoute>
          
          }/> 
          <Route path='/admin/smww-commerce/product-list'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < ManageProducts />
          </ProtectedRoute>

          }/>
          <Route path='/admin/course-mentors'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < CourseMentors />
          </ProtectedRoute>

          }/>
          <Route path='/admin/course-mentors/edit/:productId'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < CourseMentorEdit />
          </ProtectedRoute>

          }/>
          <Route path='/admin/smww-commerce/product-create'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < AddNewProduct />
          </ProtectedRoute>
          
          }/> 
           <Route path='/smww/referral/cashout/:drupal_uid'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < CashOut />
          </ProtectedRoute>
          
          }/> 
          <Route path='/apparel-order-list'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < ApparelOrderItemList />
          </ProtectedRoute>
          
          }/> 
          <Route path='/change-apparel-order-status/:order_status'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < ChangeApparelOrderStatus />
          </ProtectedRoute>
          
          }/> 
          <Route path='/apparel-order-status-log'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < ApparelOrderStatusLog />
          </ProtectedRoute>
          
          }/> 
          <Route path='/checkout-order-log'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < CheckoutOrderLog />
          </ProtectedRoute>
          
          }/> 
          
          <Route path='/admin/smww-commerce/product-categories-list'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < ManageCategories />
          </ProtectedRoute>
          
          }/> 
          <Route path='/admin/smww-commerce/product-categories-create'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < CreateProductCategory />
          </ProtectedRoute>
          
          }/> 
           <Route path='/admin/smww-commerce/product-categories-edit/:id'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < EditProductCategory />
          </ProtectedRoute>
          
          }/> 
       <Route path='/add-shipping-rates'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < FlatShippingRates />
          </ProtectedRoute>
          
          }/> 
          <Route path='/admin/smww-commerce/coupon-list'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < CouponList />
          </ProtectedRoute>
          
          }/> 
          <Route path='/admin/smww-commerce/coupon-create'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < CreateCoupon />
          </ProtectedRoute>
          
          }/> 
          <Route path='/admin/smww-commerce/coupon-edit/:id'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < EditCoupon />
          </ProtectedRoute>
          
          }/> 
          <Route path='/admin/smww-commerce/coupon-delete/:id'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < DeleteCoupon />
          </ProtectedRoute>
          
          }/> 
          <Route path='/admin/smww-commerce/discount-list'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < DiscountList />
          </ProtectedRoute>
          
          }/> 
          <Route path='/admin/smww-commerce/discount-create'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < CreateNewDiscount />
          </ProtectedRoute>
          
          }/> 
          <Route path='/admin/smww-commerce/discount-edit/:id'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < EditDiscount />
          </ProtectedRoute>
          
          }/> 
          <Route path='/admin/smww-commerce/discount-delete/:id'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < DeleteDiscount />
          </ProtectedRoute>
          
          }/> 
          <Route path='/conference-registration'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < ConferenceRegistrationReport />
          </ProtectedRoute>
          
          }/> 
        <Route path='/admin/reports/agent_athlete_scout'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < ScoutAgentAthleteExport />
          </ProtectedRoute>
          
          }/> 
        <Route path='/admin/smww-commerce/product-package-list'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < PackageList />
          </ProtectedRoute>
          
          }/> 
           <Route path='/admin/smww-commerce/product-package-edit/:id'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < EditProductPackage />
          </ProtectedRoute>
          
          }/> 
          <Route path='/admin/smww-commerce/product-package-delete/:id'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < DeleteProductPackage />
          </ProtectedRoute>
          
          }/> 
           <Route path='/admin/smww-commerce/product-package-create'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < CreateProductPackage />
          </ProtectedRoute>
          
          }/> 
        <Route path='/my_agents_account'  element={
           <ProtectedRoute roles={['agent','administrator']}>
            < AgentMyAccount />
          </ProtectedRoute>
          
          }/>
        <Route path='/my_athletes'  element={
           <ProtectedRoute roles={['agent','administrator']}>
            < ManageMyAthletes />
          </ProtectedRoute>
          
          }/>
            <Route path='/node/add/scout-report'  element={
           <ProtectedRoute roles={['administrator','SAAdmin','scout']}>
            < CreateScoutReport />
          </ProtectedRoute>
          
          }/>
          <Route path='/my_reports'  element={
           <ProtectedRoute roles={['administrator','SAAdmin','scout']}>
            < ScoutMyReports />
          </ProtectedRoute>
          
          }/>
          <Route path='/my_scouts_account'  element={
           <ProtectedRoute roles={['scout','administrator']}>
            < ScoutMyAccount />
          </ProtectedRoute>
          
          }/>
          <Route path='/my_scout_request_reports'  element={
           <ProtectedRoute roles={['scout','administrator']}>
            < MyScoutRequestReports />
          </ProtectedRoute>
          
          }/>
           <Route path='/admin/reports/scouting_request'  element={
           <ProtectedRoute roles={['scout','administrator']}>
            < ViewScoutingRequestsScout />
          </ProtectedRoute>
          
          }/>
          <Route path='/node/841/submission/:sid'  element={
           <ProtectedRoute roles={['administrator','SAAdmin','scout']}>
            < ScoutRequestReportView />
          </ProtectedRoute>
          
          }/>
          <Route path='/admin/config/content/profile-approval'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < ApprovalProfileChanges />
          </ProtectedRoute>
          
          }/>
           <Route path='/node/841/submission/:sid/:tab'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < ScoutRequestReportEdit />
          </ProtectedRoute>
          
          }/>
          <Route path='/node/841/webform-results'  element={
           <ProtectedRoute roles={['administrator','SAAdmin']}>
            < ScoutRequestWebformResult />
          </ProtectedRoute>
          
          }/>
          <Route path='/node/1080/submission/:id/:tab'  element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
           <LookingForAgentEdit />
          </ProtectedRoute>
          }/>
          <Route path='/node/1080/webform-results'  element={
          <ProtectedRoute roles={['administrator','SAAdmin','sales people']}>
           <LookingForAgentWebformResults />
          </ProtectedRoute>
          }/> 
           <Route path='/manage-course-pages'  element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
           <ManageCoursePages />
          </ProtectedRoute>
          }/>  
          <Route path='/node/add/course'  element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
           <AddNewCourse />
          </ProtectedRoute>
          }/>
          <Route path='/node/add/conferences'  element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
           <AddANewConferencePage />
          </ProtectedRoute>
          }/>
          <Route path='/manage-testimonial'  element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
           <ManageTestimonials />
          </ProtectedRoute>
          }/> 
          <Route path='/manage-success-stories'  element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
           <ManageSuccessStories />
          </ProtectedRoute>
          }/> 
          <Route path='/node/add/testimonial'  element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
           <AddTestimonialPage />
          </ProtectedRoute>
          }/>
          <Route path='/node/add/success-story'  element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
           <AddSuccessStoryPage />
          </ProtectedRoute>
          }/>
          <Route path='/page-editor' element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
           <PageEditor />
          </ProtectedRoute>
          }/>
          <Route path='/manage-visual-pages' element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
           <ManageVisualPages />
          </ProtectedRoute>
          }/>
          <Route path='/node/add/visual-page' element={
          <ProtectedRoute roles={['administrator','SAAdmin']}>
           <AddVisualPage />
          </ProtectedRoute>
          }/>
          <Route path='/global-course-content' element={
          <ProtectedRoute roles={['administrator']}>
           <GlobalCourseContent />
          </ProtectedRoute>
          }/>
          <Route path="*" element={<Page />} />
        </Routes>
        </Suspense>


        
        <Footer />
      </div>
    </Router>
     </AuthProvider>
  );
}
 
export default App;